import type { PropsElement, VolumeSource } from '../props';
import { areaOf, isMixed, materialShare, volumeOf } from '../props';
import type { ToolFn, ToolSpec } from '../types';
import { inStorey, ofType, round, storeyKey } from './select';
import { materialMatcher, normalizeType } from './vocabulary';

export const takeoffSpec: ToolSpec = {
  name: 'quantity_takeoff',
  description:
    'Sum a quantity over elements: volume (m³), area (m²) or count. Filter by IFC type, storey and material. `material` accepts a class (concrete, masonry, steel, timber, insulation, gypsum, glass), a German or English word (Beton, Stahl) or a substring of a material name (C30/37). For layered elements only the matching layers count, by thickness share; elements with several materials and no proportions (`mixed`) count in full. The result says where each volume came from (ifc base quantities, Revit properties, or the mesh) and how many matched elements had no quantity at all; always tell the visitor about `missing` and about approximations.',
  input_schema: {
    type: 'object',
    properties: {
      quantity: { type: 'string', enum: ['volume', 'area', 'count'] },
      type: { type: 'string', description: 'IFC type prefix or a word like slab, Wand' },
      storey: { type: 'string' },
      material: { type: 'string' },
    },
    required: ['quantity'],
  },
};

export interface TakeoffResult {
  quantity: 'volume' | 'area' | 'count';
  unit: 'm3' | 'm2' | null;
  total: number;
  elements: number;
  missing: number;
  sources: Partial<Record<VolumeSource, number>>;
  layer_split: number;
  mixed: number;
  type?: string;
  storey?: string;
  material?: string;
  materials_matched: string[];
  by_storey: Record<string, number>;
  by_type: Record<string, number>;
  ids: number[];
}

export const takeoff =
  (elements: PropsElement[]): ToolFn =>
  ({ quantity, type, storey, material }) => {
    const q = (String(quantity ?? 'volume') as TakeoffResult['quantity']) || 'volume';
    const matcher = materialMatcher(material);
    let hits = inStorey(ofType(elements, type), storey);
    const shares = new Map<number, number>();
    const matchedNames = new Set<string>();
    if (matcher) {
      hits = hits.filter((e) => {
        const share = materialShare(e.material, matcher.test);
        if (share <= 0) return false;
        shares.set(e.expressID, share);
        for (const n of e.material?.names ?? []) if (matcher.test(n)) matchedNames.add(n);
        return true;
      });
    }
    const result: TakeoffResult = {
      quantity: q,
      unit: q === 'volume' ? 'm3' : q === 'area' ? 'm2' : null,
      total: 0,
      elements: hits.length,
      missing: 0,
      sources: {},
      layer_split: 0,
      mixed: 0,
      ...(normalizeType(type) ? { type: normalizeType(type) } : {}),
      ...(storey ? { storey: String(storey) } : {}),
      ...(matcher ? { material: matcher.label } : {}),
      materials_matched: [...matchedNames].sort(),
      by_storey: {},
      by_type: {},
      ids: hits.map((e) => e.expressID),
    };
    if (q === 'count') {
      result.total = hits.length;
      for (const e of hits) bump(result, e, 1);
      return result;
    }
    for (const e of hits) {
      const v = q === 'volume' ? volumeOf(e) : areaOf(e);
      if (!v) {
        result.missing++;
        continue;
      }
      const share = shares.get(e.expressID) ?? 1;
      if (share < 1) result.layer_split++;
      if (matcher && isMixed(e.material)) result.mixed++;
      result.sources[v.source] = (result.sources[v.source] ?? 0) + 1;
      bump(result, e, v.value * share);
    }
    result.total = round(result.total);
    for (const k of Object.keys(result.by_storey))
      result.by_storey[k] = round(result.by_storey[k]!);
    for (const k of Object.keys(result.by_type)) result.by_type[k] = round(result.by_type[k]!);
    return result;
  };

function bump(r: TakeoffResult, e: PropsElement, value: number) {
  r.total += value;
  r.by_storey[storeyKey(e)] = (r.by_storey[storeyKey(e)] ?? 0) + value;
  r.by_type[e.type] = (r.by_type[e.type] ?? 0) + value;
}
